import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

const PROFILE_TABLE = "client_profiles";
const PROJECT_TABLE = "client_projects";
const FILE_TABLE = "client_project_files";
const UPDATE_TABLE = "client_project_updates";
const DEFAULT_BUCKET = "project-assets";

const ensureSupabase = () => {
    if (!isSupabaseConfigured || !supabase) {
        throw new Error("Supabase yapılandırması olmadan müşteri verilerini yönetemezsiniz. .env içindeki Supabase anahtarlarını doğrulayın.");
    }
};

const mapFileRecord = (record) => ({
    id: record.id,
    projectId: record.project_id,
    label: record.label,
    note: record.note ?? "",
    fileType: record.file_type ?? "other",
    sizeLabel: record.size_label ?? null,
    storageBucket: record.storage_bucket ?? DEFAULT_BUCKET,
    storagePath: record.storage_path ?? null,
    version: record.version ?? 1,
    createdAt: record.created_at ?? null,
});

const mapUpdateRecord = (record) => ({
    id: record.id,
    projectId: record.project_id,
    title: record.title,
    body: record.body ?? "",
    status: record.status ?? "bilgi",
    postedAt: record.posted_at ?? null,
});

const mapProjectRecord = (record) => ({
    id: record.id,
    clientId: record.client_id,
    title: record.title,
    status: record.status ?? "",
    summary: record.summary ?? "",
    kickoffDate: record.kickoff_date ?? null,
    deliveryDate: record.delivery_date ?? null,
    createdAt: record.created_at ?? null,
    updatedAt: record.updated_at ?? null,
    files: (record.files ?? record.client_project_files ?? []).map(mapFileRecord),
    updates: (record.updates ?? record.client_project_updates ?? []).map(mapUpdateRecord),
});

const mapProfileRecord = (record) => ({
    id: record.id,
    fullName: record.full_name ?? "",
    company: record.company ?? "",
    phone: record.phone ?? "",
    createdAt: record.created_at ?? null,
    projects: (record.projects ?? record.client_projects ?? []).map(mapProjectRecord),
});

const sanitizeFileName = (filename) => {
    const lowercased = filename.toLowerCase();
    return lowercased.replace(/[^a-z0-9_.-]+/g, "-");
};

export const fetchClientDirectory = async () => {
    ensureSupabase();
    const { data, error } = await supabase
        .from(PROFILE_TABLE)
        .select(`id, full_name, company, phone, created_at,
        projects:${PROJECT_TABLE} (
            id, client_id, title, status, summary, kickoff_date, delivery_date, created_at, updated_at,
            files:${FILE_TABLE} (id, project_id, label, note, file_type, size_label, storage_bucket, storage_path, version, created_at),
            updates:${UPDATE_TABLE} (id, project_id, title, body, status, posted_at)
        )`)
        .order("created_at", { ascending: false });

    if (error) {
        console.error("Supabase fetchClientDirectory error", error);
        throw error;
    }

    return (data ?? []).map(mapProfileRecord);
};

export const upsertClientProfileAdmin = async ({ id, fullName, company, phone }) => {
    ensureSupabase();
    const payload = {
        id,
        full_name: fullName?.trim() || null,
        company: company?.trim() || null,
        phone: phone?.trim() || null,
    };

    const { data, error } = await supabase.from(PROFILE_TABLE).upsert(payload, { onConflict: "id" }).select("id, full_name, company, phone, created_at").maybeSingle();

    if (error) {
        console.error("Supabase upsertClientProfileAdmin error", error);
        throw error;
    }

    return data ? mapProfileRecord({ ...data, projects: [] }) : mapProfileRecord(payload);
};

export const createClientProject = async ({ clientId, title, status, summary, kickoffDate, deliveryDate }) => {
    ensureSupabase();
    const { data, error } = await supabase
        .from(PROJECT_TABLE)
        .insert([
            {
                client_id: clientId,
                title: title?.trim(),
                status: status?.trim() || "",
                summary: summary?.trim() || "",
                kickoff_date: kickoffDate || null,
                delivery_date: deliveryDate || null,
            },
        ])
        .select(
            `id, client_id, title, status, summary, kickoff_date, delivery_date, created_at, updated_at,
            files:${FILE_TABLE} (id, project_id, label, note, file_type, size_label, storage_bucket, storage_path, version, created_at),
            updates:${UPDATE_TABLE} (id, project_id, title, body, status, posted_at)`
        )
        .maybeSingle();

    if (error) {
        console.error("Supabase createClientProject error", error);
        throw error;
    }

    return data ? mapProjectRecord(data) : null;
};

export const updateClientProject = async ({ id, clientId, title, status, summary, kickoffDate, deliveryDate }) => {
    ensureSupabase();
    const payload = {
        title: title?.trim(),
        status: status?.trim() || "",
        summary: summary?.trim() || "",
        kickoff_date: kickoffDate || null,
        delivery_date: deliveryDate || null,
    };

    const { data, error } = await supabase
        .from(PROJECT_TABLE)
        .update(payload)
        .eq("id", id)
        .eq("client_id", clientId)
        .select(
            `id, client_id, title, status, summary, kickoff_date, delivery_date, created_at, updated_at,
            files:${FILE_TABLE} (id, project_id, label, note, file_type, size_label, storage_bucket, storage_path, version, created_at),
            updates:${UPDATE_TABLE} (id, project_id, title, body, status, posted_at)`
        )
        .maybeSingle();

    if (error) {
        console.error("Supabase updateClientProject error", error);
        throw error;
    }

    return data ? mapProjectRecord(data) : null;
};

export const deleteClientProject = async (projectId) => {
    ensureSupabase();
    const { error } = await supabase.from(PROJECT_TABLE).delete().eq("id", projectId);
    if (error) {
        console.error("Supabase deleteClientProject error", error);
        throw error;
    }
};

export const createProjectUpdate = async ({ projectId, title, body, status }) => {
    ensureSupabase();
    const { data, error } = await supabase
        .from(UPDATE_TABLE)
        .insert([
            {
                project_id: projectId,
                title: title?.trim(),
                body: body?.trim() || "",
                status: status?.trim() || "bilgi",
            },
        ])
        .select("id, project_id, title, body, status, posted_at")
        .maybeSingle();

    if (error) {
        console.error("Supabase createProjectUpdate error", error);
        throw error;
    }

    return data ? mapUpdateRecord(data) : null;
};

export const deleteProjectUpdate = async (updateId) => {
    ensureSupabase();
    const { error } = await supabase.from(UPDATE_TABLE).delete().eq("id", updateId);
    if (error) {
        console.error("Supabase deleteProjectUpdate error", error);
        throw error;
    }
};

export const uploadProjectAsset = async ({ file, clientId, projectId, bucket = DEFAULT_BUCKET }) => {
    ensureSupabase();
    if (!file) {
        throw new Error("Yüklenecek dosya seçilmedi.");
    }
    const safeName = sanitizeFileName(file.name || "asset.bin");
    const path = `${clientId}/${projectId}/${Date.now()}-${safeName}`;
    const { error } = await supabase.storage.from(bucket).upload(path, file, {
        cacheControl: "3600",
        upsert: true,
    });

    if (error) {
        console.error("Supabase uploadProjectAsset error", error);
        throw error;
    }

    return { bucket, path };
};

export const createProjectFileRecord = async ({ projectId, label, note, fileType, sizeLabel, storageBucket = DEFAULT_BUCKET, storagePath, version = 1 }) => {
    ensureSupabase();
    const { data, error } = await supabase
        .from(FILE_TABLE)
        .insert([
            {
                project_id: projectId,
                label: label?.trim(),
                note: note?.trim() || null,
                file_type: fileType?.trim() || null,
                size_label: sizeLabel?.trim() || null,
                storage_bucket: storageBucket,
                storage_path: storagePath,
                version,
            },
        ])
        .select("id, project_id, label, note, file_type, size_label, storage_bucket, storage_path, version, created_at")
        .maybeSingle();

    if (error) {
        console.error("Supabase createProjectFileRecord error", error);
        throw error;
    }

    return data ? mapFileRecord(data) : null;
};

export const deleteProjectFile = async ({ fileId, storageBucket = DEFAULT_BUCKET, storagePath, removeAsset = true }) => {
    ensureSupabase();
    const { error } = await supabase.from(FILE_TABLE).delete().eq("id", fileId);
    if (error) {
        console.error("Supabase deleteProjectFile error", error);
        throw error;
    }

    if (removeAsset && storagePath) {
        const { error: storageError } = await supabase.storage.from(storageBucket).remove([storagePath]);
        if (storageError) {
            console.warn("Supabase storage dosyası silinemedi", storageError);
        }
    }
};